<script setup lang="ts">
import { computed, ref, type PropType, onMounted, watch } from 'vue'
import TransitFormDialog from '@/components/funding_forms/TransitFormDialog.vue'
import VericheckFormDialog from '@/components/funding_forms/VericheckFormDialog.vue'
import TsysAchFormDialog from '@/components/funding_forms/TsysAchFormDialog.vue'
import AuthorizeNetFormDialog from '@/components/funding_forms/AuthorizeNetFormDialog.vue'
import { useStore } from '@/stores'
import { API } from '@/modules/api'

const props = defineProps({
  fundingSources: {
    type: Array as PropType<TFundingSource[] | null>,
    required: true
  },
  selectedFundingSourceId: {
    type: [Number, null] as PropType<number | null>,
    required: true
  },
  district: {
    type: Object as PropType<TDistrict>,
    required: true
  }
})

const store = useStore()

// Define reactive data
const showTransitFormDialog = ref(false)
const showVericheckFormDialog = ref(false)
const showTsysAchFormDialog = ref(false)
const showAuthorizeNetFormDialog = ref(false)

const emit = defineEmits(['select-funding-source'])
const localSelectedFundingSourceId = ref(props.selectedFundingSourceId)

// Keep local state in sync with prop changes
watch(
  () => props.selectedFundingSourceId,
  newVal => {
    localSelectedFundingSourceId.value = newVal
  }
)

// DTA-979: Authorize.Net is one hosted form for card, bank account or both, so an
// Authorize.Net-only district gets a single tile naming both rather than "Credit Card".
const authorizeOnly = computed(
  () =>
    !!props.district.hasAuthorize &&
    !props.district.hasTransit &&
    !props.district.hasVericheck &&
    !props.district.hasTsysAch
)

const displayCreditCardForm = () => {
  if (props.district.hasTransit) {
    showTransitFormDialog.value = true
  } else if (props.district.hasAuthorize) {
    showAuthorizeNetFormDialog.value = true
  }
}

// One "Bank Account" card serves both bank providers (VeriCheck XOR TSYS ACH).
const displayBankAccountForm = () => {
  // VeriCheck wins when a misconfigured district somehow has both flags on.
  // This MUST match core, which hides TSYS ACH funding sources whenever the
  // district has VeriCheck (FamilyUserFundingSourceRepository / GenericController).
  // Preferring TSYS ACH here instead let a family complete a capture — token
  // minted, vault row written — whose funding source then never appeared in the
  // chooser, while the working VeriCheck path stopped being offered at all.
  if (props.district.hasVericheck) {
    showVericheckFormDialog.value = true
  } else {
    showTsysAchFormDialog.value = true
  }
}

const selectFundingSource = () => {
  emit('select-funding-source', localSelectedFundingSourceId.value)
}

const handleAuthorizeNetSavedFundingSources = async () => {
  try {
    await API.Get.syncFundingSources()
    await handleSavedFundingSources()
  } catch (err) {
    console.error(err)
  }
}

const handleSavedFundingSources = async () => {
  try {
    const results = await API.Get.fundingSources(props.district.districtId)
    store.saveFundingSources(results, props.district.districtId)
    const savedSources = store.fundingSources[props.district.districtId]
    if (savedSources && savedSources.length > 0) {
      localSelectedFundingSourceId.value = savedSources[0].fundingSourceId
      emit('select-funding-source', localSelectedFundingSourceId.value)
    }
  } catch (err) {
    console.error(err)
  }
}
</script>
<template>
  <TransitFormDialog
    v-model="showTransitFormDialog"
    :transit-url="district.extTransitUrl"
    :transit-url-with-manifest="district.transitUrlWithManifest"
    :environment-id="district.environmentId"
    @funding-info-saved="handleSavedFundingSources"
  />
  <VericheckFormDialog
    v-model="showVericheckFormDialog"
    :district-id="district.districtId"
    :environment-id="district.environmentId"
    @funding-info-saved="handleSavedFundingSources"
  />
  <TsysAchFormDialog
    v-model="showTsysAchFormDialog"
    :district-id="district.districtId"
    @funding-info-saved="handleSavedFundingSources"
  />
  <AuthorizeNetFormDialog
    v-model="showAuthorizeNetFormDialog"
    :district-id="district.districtId"
    @authorize-net-funding-info-saved="handleAuthorizeNetSavedFundingSources"
  />
  <div v-if="fundingSources && fundingSources.length === 0">
    <div class="w-full text-center font-bold mb-4">
      {{ $t('c.nutrition.fundingSource.SelectFundingSourceType') }}
    </div>
    <div class="fs-payment-providers">
      <a
        v-if="authorizeOnly"
        href=""
        class="fs-payment-providers__unified"
        @click.prevent="displayCreditCardForm"
      >
        <div class="fs-payment-providers__logo">
          <img src="/images/fs/credit_card.svg" />
          <img src="/images/fs/bank_account.svg" />
        </div>
        <div class="fs-payment-providers__name">
          {{ $t('c.nutrition.fundingSource.creditCard') }} /
          {{ $t('c.nutrition.fundingSource.bankAccount') }}
        </div>
      </a>
      <a
        v-else-if="district.hasTransit || district.hasAuthorize"
        href=""
        @click.prevent="displayCreditCardForm"
      >
        <div class="fs-payment-providers__logo">
          <img src="/images/fs/credit_card.svg" />
        </div>
        <div class="fs-payment-providers__name">
          {{ $t('c.nutrition.fundingSource.creditCard') }}
        </div>
      </a>
      <a
        v-if="district.hasVericheck || district.hasTsysAch"
        href=""
        @click.prevent="displayBankAccountForm"
      >
        <div class="fs-payment-providers__logo">
          <img src="/images/fs/bank_account.svg" />
        </div>
        <div class="fs-payment-providers__name">
          {{ $t('c.nutrition.fundingSource.bankAccount') }}
        </div>
      </a>
    </div>
  </div>
  <div v-else>
    <div class="mb-3">
      {{ $t('c.nutrition.fundingSource.selectFundingSource') }}
    </div>
    <div
      v-for="source in fundingSources"
      :key="source.fundingSourceId"
      class="flex items-center gap-2 mb-2"
    >
      <RadioButton
        v-model="localSelectedFundingSourceId"
        :value="source.fundingSourceId"
        :inputId="`funding-${source.fundingSourceId}`"
        @change="selectFundingSource"
      />
      <label :for="`funding-${source.fundingSourceId}`">
        {{ `${source.type.replace('_', ' ')} ending in ${source.last4}` }}
      </label>
    </div>
  </div>
</template>

<style scoped>
.fs-payment-providers {
  display: flex;
  width: 100%;
  justify-content: center;
}

.fs-payment-providers a {
  width: 10em;
  height: 10em;
  background-color: #08386a;
  color: white;
  -webkit-border-radius: 1em;
  -moz-border-radius: 1em;
  border-radius: 1em;
  text-decoration: none;
}

.fs-payment-providers a:hover {
  background-color: #084e8b;
}

.fs-payment-providers a:not(:first-child) {
  margin-left: 1em;
}

.fs-payment-providers__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 8em;
  width: 100%;
}

.fs-payment-providers__logo img {
  object-fit: contain;
  height: 70%;
  width: 70%;
}

/* DTA-979: one tile for Authorize.Net, showing both method icons side by side */
.fs-payment-providers a.fs-payment-providers__unified {
  width: 16em;
}

.fs-payment-providers__unified .fs-payment-providers__logo img {
  width: 38%;
}

.fs-payment-providers__name {
  text-align: center;
  height: 3em;
  color: white;
  font-size: 1.1em;
  font-weight: bold;
}
</style>
